import moment from 'moment';
import 'moment-timezone';
import { ReactComponent as ArrowLeft } from '../../../assets/img/icons/datepicker/ArrowLeft.svg';
import { ReactComponent as ArrowRight } from '../../../assets/img/icons/datepicker/ArrowRight.svg';
import { intervalsDate } from '../../../constants/analytics.constants';
import { getLocalTimestamp } from '../../../utils/commonMethods';
import { checkSearchParameter, getSearchParameter, hasTodayQueryParam } from '../../../utils/urlFunctions';
import '../DateTimepicker.scss';

export const CustomDateArrow = ({
  start,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  onChange,
  end,
  withoutTime = false,
  timeInterval = null,
  disabled = false,
  reverse = false,
  allowFuture = false,
  closedDays,
}) => {
  const countInterval = (startDate, interval, revert = false) => {
    let difference = startDate;
    if (revert) {
      return moment(difference).diff(interval, 'milliseconds');
    } else {
      return moment(difference).add(interval, 'milliseconds');
    }
  };

  const onChangeAction = (startDate, endDate = null, arrowInterval = null) => {
    onChange(startDate, endDate, arrowInterval);
  };

  const getMomentNow = () => {
    return moment(Number(getLocalTimestamp(Date.now(), timezone)));
  };

  const isStartOfDay = momentDate => {
    return momentDate.format('HH:mm:ss.SSS') === '00:00:00.000';
  };

  const isEndOfDay = momentDate => {
    return momentDate.format('HH:mm:ss.SSS') === '23:59:59.999';
  };

  const hasWorkingDay = range => {
    if (!closedDays?.length) {
      return true;
    }

    const rangeDate = range.start.clone();

    while (rangeDate.isSameOrBefore(range.end, 'day')) {
      if (!closedDays.includes(rangeDate.day())) {
        return true;
      }

      rangeDate.add(1, 'day');
    }

    return false;
  };

  const getPrevDaysRange = (momentStart, momentEnd) => {
    const diffDays = momentEnd.diff(momentStart, 'days');

    const buildRange = nextEnd => {
      const end = nextEnd.clone().subtract(1, 'day').endOf('day');
      const start = end.clone().subtract(diffDays, 'days').startOf('day');

      return { start, end };
    };

    let range = buildRange(momentStart);

    if (closedDays?.length && diffDays < 7) {
      const stepDays = diffDays + 1;

      for (let offset = 0; offset < 7; offset += stepDays) {
        if (hasWorkingDay(range)) {
          break;
        }

        range = buildRange(range.start);
      }
    }

    return range;
  };

  const getNextDaysRange = (momentStart, momentEnd) => {
    const momentNow = getMomentNow();
    const diffDays = momentEnd.diff(momentStart, 'days');

    const buildRange = nextStart => {
      const start = nextStart.clone().add(1, 'day').startOf('day');
      const end = start.clone().add(diffDays, 'days').endOf('day');

      return { start, end };
    };

    const fixEndToNow = range => {
      if (!allowFuture && range.end.isSameOrAfter(momentNow, 'day')) {
        if (range.start.isSameOrAfter(momentNow, 'day')) {
          return { start: 'today', end: 'today' };
        }

        return { start: range.start, end: momentNow.clone() };
      }

      return range;
    };

    let range = buildRange(momentEnd);
    range = fixEndToNow(range);

    if (range.start === 'today') {
      return range;
    }

    if (closedDays?.length && diffDays < 7) {
      const stepDays = diffDays + 1;

      for (let offset = 0; offset < 7; offset += stepDays) {
        if (hasWorkingDay(range)) {
          break;
        }

        range = buildRange(range.end);
        range = fixEndToNow(range);

        if (range.start === 'today') {
          break;
        }
      }
    }

    return range;
  };

  const getPrevMinutesRange = (momentStart, momentEnd) => {
    const rangeMomentEnd = isEndOfDay(momentEnd) ? momentEnd.clone().add(1, 'day').startOf('day') : momentEnd;
    const diffMinutes = rangeMomentEnd.diff(momentStart, 'minutes');

    let range = {
      end: momentStart.clone(),
      start: momentStart.clone().subtract(diffMinutes, 'minutes'),
    };

    if (isStartOfDay(range.end)) {
      range.end = range.end.clone().subtract(1, 'day').endOf('day');
    }

    if (closedDays?.length) {
      for (let offset = 0; offset < 7; offset++) {
        if (hasWorkingDay(range)) {
          break;
        }

        range = {
          end: range.start.clone().subtract(1, 'day').endOf('day'),
          start: range.start.clone().startOf('day').subtract(diffMinutes, 'minutes'),
        };
      }
    }

    return range;
  };

  const getNextMinutesRange = (momentStart, momentEnd) => {
    const momentNow = getMomentNow();
    const rangeMomentEnd = isEndOfDay(momentEnd) ? momentEnd.clone().add(1, 'day').startOf('day') : momentEnd;
    const diffMinutes = rangeMomentEnd.diff(momentStart, 'minutes');

    let range = {
      start: rangeMomentEnd.clone(),
      end: rangeMomentEnd.clone().add(diffMinutes, 'minutes'),
    };

    if (isStartOfDay(range.end)) {
      range.end = range.end.clone().subtract(1, 'day').endOf('day');
    }

    if (range.end.isSameOrAfter(momentNow, 'minutes')) {
      range.end = momentNow.clone();

      if (
        range.start.isAfter(momentNow, 'day') ||
        (range.start.isSame(momentNow, 'day') && isStartOfDay(range.start))
      ) {
        range.start = 'today';
        range.end = 'today';
      }

      return range;
    }

    if (closedDays?.length) {
      for (let i = 0; i < 7; i++) {
        if (hasWorkingDay(range)) {
          break;
        }

        const newRangeStart = range.end.clone().add(1, 'day').startOf('day');
        const newRangeEnd = newRangeStart.clone().add(diffMinutes, 'minutes');

        range = {
          start: newRangeStart,
          end: newRangeEnd,
        };

        if (isStartOfDay(range.end)) {
          range.end = range.end.clone().subtract(1, 'day').endOf('day');
        }

        if (range.end.isSameOrAfter(momentNow, 'minutes')) {
          range.end = momentNow.clone();

          if (
            range.start.isAfter(momentNow, 'day') ||
            (range.start.isSame(momentNow, 'day') && isStartOfDay(range.start))
          ) {
            range.start = 'today';
            range.end = 'today';
          }

          break;
        }
      }
    }

    return range;
  };

  const changeLeftRange = () => {
    if (timeInterval !== null) {
      if (withoutTime) {
        onChangeAction(
          countInterval(
            getLocalTimestamp(Date.now(), timezone) - timeInterval.value * 1000,
            timeInterval.value * 1000,
            true
          ),
          Number(getLocalTimestamp(Date.now(), timezone) - timeInterval.value * 1000),
          timeInterval.icon
        );
      } else {
        const momentEnd = moment(Number(getLocalTimestamp(Date.now(), timezone)));
        const momentStart = momentEnd.clone().subtract(timeInterval.value, 'seconds');

        if (isStartOfDay(momentStart) && isEndOfDay(momentEnd)) {
          const prevDaysRange = getPrevDaysRange(momentStart, momentEnd);
          onChangeAction(prevDaysRange.start, prevDaysRange.end, timeInterval.icon);
        } else {
          const prevMinutesRange = getPrevMinutesRange(momentStart, momentEnd);
          onChangeAction(prevMinutesRange.start, prevMinutesRange.end, timeInterval.icon);
        }
      }
    } else if (start !== null && start !== 'select' && end !== null) {
      let datesDifference = (moment(end).unix() - moment(start).unix()) * 1000;

      if (
        moment('2019-01-01').isBefore(start, 'day') &&
        moment('2019-01-01').isBefore(moment(start).diff(datesDifference), 'day')
      ) {
        if (checkSearchParameter('interval')) {
          if (withoutTime) {
            let interval = countInterval(
              start,
              intervalsDate.find(it => it.icon === getSearchParameter('interval')).value * 1000,
              true
            );

            onChangeAction(new Date(interval), start, getSearchParameter('interval'));
          } else {
            const interval = intervalsDate.find(it => it.icon === getSearchParameter('interval'));
            const momentStart = moment(start);
            const momentEnd = interval ? momentStart.clone().add(interval.value, 'seconds') : moment(end);
            const prevMinutesRange = getPrevMinutesRange(momentStart, momentEnd);

            onChangeAction(prevMinutesRange.start, prevMinutesRange.end, getSearchParameter('interval'));
          }
        } else {
          if (withoutTime) {
            let startDate = moment(start).isSame(moment(end), 'date')
              ? moment(start).subtract(1, 'day')
              : start;

            onChangeAction(new Date(countInterval(startDate, datesDifference, true)), startDate);
          } else {
            const momentStart = moment(start);
            const momentEnd = moment(end);

            if (hasTodayQueryParam() || (isStartOfDay(momentStart) && isEndOfDay(momentEnd))) {
              const prevDaysRange = getPrevDaysRange(momentStart, momentEnd);
              onChangeAction(prevDaysRange.start, prevDaysRange.end);
            } else {
              const prevMinutesRange = getPrevMinutesRange(momentStart, momentEnd);
              onChangeAction(prevMinutesRange.start, prevMinutesRange.end);
            }
          }
        }
      } else {
        onChangeAction(new Date(2019, 0, 1), start, getSearchParameter('interval'));
      }
    }
  };

  const changeRightRange = () => {
    if (
      start !== 'select' &&
      ((start !== null && end !== null && timeInterval === null) ||
        (allowFuture && (start !== null || timeInterval !== null)))
    ) {
      if (checkSearchParameter('interval')) {
        let nextDate = countInterval(
          end,
          intervalsDate.find(it => it.icon === getSearchParameter('interval')).value * 1000
        );

        if (
          moment(getLocalTimestamp(Date.now(), timezone)).isSameOrBefore(
            nextDate,
            withoutTime ? 'day' : 'minute'
          ) &&
          !allowFuture
        ) {
          onChangeAction(getSearchParameter('interval'));
        } else {
          if (withoutTime) {
            onChangeAction(end, nextDate, getSearchParameter('interval'));
          } else {
            const searchInterval = getSearchParameter('interval');
            const interval = intervalsDate.find(item => item.icon === searchInterval);
            const momentStart = moment(start);
            const momentEnd = interval ? momentStart.clone().add(interval.value, 'seconds') : moment(end);
            const nextMinutesRange = getNextMinutesRange(momentStart, momentEnd);

            onChangeAction(nextMinutesRange.start, nextMinutesRange.end, getSearchParameter('interval'));
          }
        }
      } else if (end !== null && start !== null) {
        if (withoutTime) {
          let datesDifference = (moment(end).unix() - moment(start).unix()) * 1000;

          let ifNextIsTomorrow =
            !allowFuture &&
            moment(getLocalTimestamp(Date.now(), timezone)).isSameOrBefore(
              countInterval(end, datesDifference),
              'day'
            );

          let endDate =
            !ifNextIsTomorrow && moment(start).isSame(moment(end), 'date') ? moment(end).add(1, 'day') : end;

          onChangeAction(
            endDate,
            ifNextIsTomorrow
              ? getLocalTimestamp(Date.now(), timezone)
              : countInterval(endDate, datesDifference)
          );
        } else {
          const momentStart = moment(start);
          const momentEnd = moment(end);

          if (isStartOfDay(momentStart) && isEndOfDay(momentEnd)) {
            const nextDaysRange = getNextDaysRange(momentStart, momentEnd);
            onChangeAction(nextDaysRange.start, nextDaysRange.end);
          } else {
            const nextMinutesRange = getNextMinutesRange(momentStart, momentEnd);
            onChangeAction(nextMinutesRange.start, nextMinutesRange.end);
          }
        }
      } else if (allowFuture && timeInterval !== null) {
        onChangeAction(
          getLocalTimestamp(Date.now(), timezone),
          countInterval(getLocalTimestamp(Date.now(), timezone), timeInterval.value * 1000),
          timeInterval.icon
        );
      }
    }
  };

  const changeRange = () => {
    if (reverse) {
      changeLeftRange();
    } else changeRightRange();
  };

  return (
    <div
      className={`datepicker__arrow${disabled ? ' datepicker__arrow__disabled' : ''}`}
      onClick={() => {
        changeRange();
      }}
    >
      {reverse ? <ArrowLeft /> : <ArrowRight />}
    </div>
  );
};
